// src/store/authStore.js
import { create } from 'zustand'
import api from '@/utils/axios'
import { ROLES } from '@/constants/roles'
import { setUser as setSentryUser } from '@/utils/sentry'
import { initEcho, disconnectEcho } from '@/utils/echo'

const useAuthStore = create((set, get) => ({
  user:        JSON.parse(localStorage.getItem('educza_user') || 'null'),
  tenant:      null,
  token:       localStorage.getItem('educza_token') || null,
  initialized: !localStorage.getItem('educza_token'),
  loading:     false,
  error:       null,

  // ─── REGISTER ───────────────────────────────────────────────────────────
  register: async (formData) => {
    set({ loading: true, error: null })
    try {
      const { data } = await api.post('/auth/register', formData)
      get()._saveSession(data)
      return { success: true, data }
    } catch (err) {
      const error = err.response?.data?.message || 'Registration failed'
      set({ error, loading: false })
      return { success: false, error }
    }
  },

  // ─── LOGIN (email + password) ────────────────────────────────────────────
  login: async ({ email, password }) => {
    set({ loading: true, error: null })
    try {
      const { data } = await api.post('/auth/login', { email, password })
      get()._saveSession(data)
      return { success: true, data }
    } catch (err) {
      const error = err.response?.data?.message || 'Invalid email or password'
      set({ error, loading: false })
      return { success: false, error }
    }
  },

  // ─── OTP LOGIN ────────────────────────────────────────────────────────────
  sendOtp: async (phone) => {
    try {
      await api.post('/auth/otp/send', { phone })
      return { success: true }
    } catch (err) {
      return { success: false, error: err.response?.data?.message }
    }
  },

  verifyOtp: async ({ phone, otp }) => {
    set({ loading: true, error: null })
    try {
      const { data } = await api.post('/auth/otp/verify', { phone, otp })
      get()._saveSession(data)
      return { success: true, data }
    } catch (err) {
      const error = err.response?.data?.message || 'Invalid OTP'
      set({ error, loading: false })
      return { success: false, error }
    }
  },

  // ─── LOGOUT ──────────────────────────────────────────────────────────────
  logout: async () => {
    try {
      await api.post('/auth/logout')
    } catch (_) {
      // ignore — clear local state regardless
    } finally {
      disconnectEcho()
      localStorage.removeItem('educza_token')
      localStorage.removeItem('educza_user')
      set({ user: null, tenant: null, token: null, initialized: true })
      setSentryUser(null)
    }
  },

  // ─── FETCH CURRENT USER ───────────────────────────────────────────────────
  fetchMe: async () => {
    if (!get().token) {
      set({ initialized: true })
      return
    }

    set({ loading: true, error: null })

    try {
      const { data } = await api.get('/auth/me')
      set({ user: data.user, tenant: data.user.tenant, initialized: true, loading: false })
      localStorage.setItem('educza_user', JSON.stringify(data.user))
      setSentryUser(data.user)
      initEcho(get().token)
    } catch (_) {
      await get().logout()
      set({ initialized: true, loading: false })
    }
  },

  // ─── HELPERS ─────────────────────────────────────────────────────────────
  _saveSession: ({ token, user }) => {
    localStorage.setItem('educza_token',  token)
    localStorage.setItem('educza_user', JSON.stringify(user))
    set({ token, user, tenant: user?.tenant, loading: false, initialized: true })
    // Analytics + realtime are non-critical: never let them break auth flow.
    try { setSentryUser(user) } catch (_) { /* ignore */ }
    try { initEcho(token) } catch (_) { /* ignore */ }
  },

  isAuthenticated: () => !!get().token,

  isAdmin:      () => get().user?.role === ROLES.ADMIN,
  isTeacher:    () => get().user?.role === ROLES.TEACHER,
  isParent:     () => get().user?.role === ROLES.PARENT,
  isStudent:    () => get().user?.role === ROLES.STUDENT,
  isSuperAdmin: () => get().user?.role === ROLES.SUPER_ADMIN,
}))

export default useAuthStore
